import { call, delay, put, select } from 'redux-saga/effects'

import { FETCH_OPENING_GROUPS_SUCCESS } from '../../../actions/drawable'
import { getProjectById } from '../../../api/projects-api'
import { fetchOpeningGroupsByProjectId } from '../../../api/takeoff-api'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import { Project } from '../../../models/project'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { PROJECT_STATUS_NAME } from '../../../shared/constants/projectStatusName'
import { polylineInsidePolygon } from '../../../utils/coordinates/defineLocationRegion'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { preparedDrawablesSuccess } from '../../slices/2D'
import { setInfoModalData, setLoadingText } from '../../slices/common'
import { selectActiveClassificationDocumentChunkId } from '../../slices/documents'
import { selectHasGeometriesInRegion } from '../../slices/geometry'
import { selectProject } from '../../slices/project'
import { selectMeasurementToolObjectsInRegion } from '../../slices/tools'
import { Coordinates2D, IMUP2DDrawableLocation } from '../../types'
import { convertOpeningGroupsToDrawableLocations } from '../2D/data-prep/convertOpeningsToDrawableLocations'
import { regenerateMeasurementsByProjectAndDocumentIds } from '../2D/saveUpdatedCalibrationFactor'

const JOIST_SYSTEMS = [
    DRAWABLE_TYPES.FLOOR_SYSTEM.toString(),
    DRAWABLE_TYPES.FLOOR_SYSTEMS.toString(),
    DRAWABLE_TYPES.ROOF_SYSTEM.toString(),
    DRAWABLE_TYPES.ROOF_SYSTEMS.toString(),
]

function* updateContextMarkups(projectId: number) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    const activeClassificationChunkId: number | null = yield select(selectActiveClassificationDocumentChunkId)

    if (!manager || !activeClassificationChunkId) return

    yield regenerateMeasurementsByProjectAndDocumentIds(manager, projectId, activeClassificationChunkId)
}

export function* pollProjectForRegionUpdate(
    loadingText: string,
    delayMS = 5000,
    origOpeningGroups: OpeningGroupAPI[],
    regionScaleChanged: boolean,
    regionId?: number,
    regionCoordinates?: Coordinates2D
) {
    try {
        const hasGeometriesInRegion: boolean = yield select(selectHasGeometriesInRegion, regionId, regionCoordinates)
        const hasMeasurementToolsInRegion = yield select(
            selectMeasurementToolObjectsInRegion,
            regionId,
            regionCoordinates
        )

        const project: Project = yield select(selectProject)

        //check if any Joists affected by region change
        if (regionCoordinates && (hasGeometriesInRegion || hasMeasurementToolsInRegion)) {
            const dblJoistChange = hasGeometriesInRegion
                ? origOpeningGroups.some((og) => {
                      if (JOIST_SYSTEMS.includes(og.type)) {
                          return og.openings.some((o) => {
                              return o.opening_locations.some((ol) => {
                                  const isInside = polylineInsidePolygon(
                                      ol.coordinates,
                                      regionCoordinates.map((c) => [c[0], c[1]])
                                  )
                                  const regionsMatch = ol.region_id === regionId

                                  if (!isInside && regionsMatch) {
                                      return o.opening_locations.some((location) => {
                                          return location?.additional_data?.is_double_joist
                                      })
                                  } else if (isInside && !regionsMatch) {
                                      return o.opening_locations.some((location) => {
                                          return location?.additional_data?.is_double_joist
                                      })
                                  } else if (isInside && regionScaleChanged) {
                                      return o.opening_locations.some((location) => {
                                          return location?.additional_data?.is_double_joist
                                      })
                                  }

                                  return false
                              })
                          })
                      }

                      return false
                  })
                : false

            yield put(setLoadingText(loadingText))

            yield delay(delayMS)

            while (true) {
                const projectData: Project = yield call(getProjectById, project.id)

                if (projectData.projectStatus.name !== PROJECT_STATUS_NAME.CALCULATION_PENDING) {
                    if (hasGeometriesInRegion) {
                        const openingGroups: OpeningGroupAPI[] = yield call(fetchOpeningGroupsByProjectId, project.id)

                        yield put({ type: FETCH_OPENING_GROUPS_SUCCESS, payload: openingGroups })

                        const drawableLocations: IMUP2DDrawableLocation[] = yield call(
                            convertOpeningGroupsToDrawableLocations,
                            openingGroups
                        )

                        const documentChunkIds: number[] = [
                            ...new Set(drawableLocations.map((location) => location.document_chunk_id)),
                        ]

                        yield put(preparedDrawablesSuccess({ drawableLocations, documentChunkIds }))

                        if (dblJoistChange) {
                            yield put(
                                setInfoModalData({
                                    visible: true,
                                    title: 'Double Joist',
                                    body: 'This region contains Double Joist. Changing the region removed the Double Joist.',
                                })
                            )
                        }
                    }

                    yield updateContextMarkups(project.id)

                    break
                }

                yield delay(delayMS)
            }
        }
    } catch (error) {
        console.error('Error from polling project', error)
    } finally {
        yield put(setLoadingText(null))
    }
}
